import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { Button, CampaignNav, emitUnhandledError, Glyphs, logger, Tooltip } from '@/ui'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import {
	campaignAclIndexQueryKey,
	type CampaignUserData,
	roleEnum,
	useCampaignAclDestroy,
	useCampaignAclIndex,
	useCampaignAclStore,
	useOrganisationMembershipsIndex,
	useOrganisationRelationShow,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const TRACKING_SOURCE = 'access-page'

function displayName(user: { firstname: string | null; lastname: string | null; email: string }): string {
	if (user.firstname || user.lastname) {
		return [user.firstname, user.lastname].filter(Boolean).join(' ')
	}
	return user.email
}

function roleLabel(role: string | null | undefined): string {
	if (!role) { return '—' }
	return role.charAt(0).toUpperCase() + role.slice(1)
}

export type CampaignAccessProps = {
	campaignId: string
}

export function CampaignAccess({ campaignId }: CampaignAccessProps) {
	const { t } = useTranslation('common')
	const { user, can } = useAuth()
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const { data: orgData } = useOrganisationRelationShow(organisationId)
	const aclEnabled = orgData?.accessMode === 'explicit'
	const canManage = can('campaign-access:update')
	const showAclList = aclEnabled || canManage
	const [grantDialogOpen, setGrantDialogOpen] = useState(false)

	const { data: aclData, isLoading, isFetching } = useCampaignAclIndex(
		campaignId,
		{ perPage: 100, page: 1 },
		{ enabled: showAclList },
	)

	const invalidateAcl = () => void queryClient.invalidateQueries({ queryKey: campaignAclIndexQueryKey(campaignId) })
	const grantAccess = useCampaignAclStore({
		onSuccess: invalidateAcl,
		onError: (error) => {
			emitUnhandledError({
				title: t('overview.access.grantFailed'),
				description: error instanceof Error ? error.message : undefined,
				cause: error,
			})
		},
	})
	const revokeAccess = useCampaignAclDestroy({
		onSuccess: invalidateAcl,
		onError: (error) => {
			emitUnhandledError({
				title: t('overview.access.revokeFailed'),
				description: error instanceof Error ? error.message : undefined,
				cause: error,
			})
		},
	})

	const accessibleUsers = (aclData?.data ?? []).filter((accessibleUser) => accessibleUser.role !== 'jobillan')
	const accessibleUserIds = new Set(accessibleUsers.map((accessibleUser) => accessibleUser.id))

	const handleRevoke = (accessibleUser: CampaignUserData) => {
		logger.track('Campaign access: Revoked', {
			data: {
				source: TRACKING_SOURCE,
				campaign_id: campaignId,
				user_id: accessibleUser.id,
			},
		})

		revokeAccess.mutate({
			campaign: campaignId,
			user: accessibleUser.id,
		})
	}

	const handleGrant = (member: { id: string }) => {
		logger.track('Campaign access: Granted', {
			data: {
				source: TRACKING_SOURCE,
				campaign_id: campaignId,
				user_id: member.id,
			},
		})

		grantAccess.mutate({
			campaign: campaignId,
			user: member.id,
		})
	}

	return (
		<CampaignNav activeCampaignNavLink="access" whiteBackground>
			<div className="py-10">
				<div className="flex items-center justify-between mb-6">
					<h2 className="text-xl font-semibold">{t('overview.nav.access')}</h2>
					{canManage && (
						<Button
							type="colorful"
							size="short"
							prefix="plus"
							onClick={() => setGrantDialogOpen(true)}
						>
							{t('overview.access.grantAccess')}
						</Button>
					)}
				</div>
				{showAclList && !aclEnabled && (
					<p className="mb-4 text-sm text-grey-600">{t('overview.access.implicitNotice')}</p>
				)}
				{!showAclList
					? (
						<div className="bg-white rounded-lg border border-grey-200 py-12 text-center text-sm text-grey-500">
							{t('overview.access.allUsersHaveAccess')}
						</div>
					)
					: (
						<AccessibleUsersTable
							users={accessibleUsers}
							explicitMode={aclEnabled}
							selfId={user?.id ?? null}
							isLoading={isLoading}
							isFetching={isFetching}
							canManage={canManage}
							pendingUserId={revokeAccess.isPending ? revokeAccess.variables?.user ?? null : null}
							disableActions={revokeAccess.isPending}
							onRevoke={handleRevoke}
						/>
					)}
			</div>
			<GrantAccessDialog
				open={grantDialogOpen}
				onClose={() => setGrantDialogOpen(false)}
				enabled={canManage}
				explicitMode={aclEnabled}
				accessibleUserIds={accessibleUserIds}
				pendingUserId={grantAccess.isPending ? grantAccess.variables?.user ?? null : null}
				disableActions={grantAccess.isPending}
				onGrant={handleGrant}
			/>
		</CampaignNav>
	)
}

type AccessibleUsersTableProps = {
	users: CampaignUserData[]
	explicitMode: boolean
	selfId: string | null
	isLoading: boolean
	isFetching: boolean
	canManage: boolean
	pendingUserId: string | null
	disableActions: boolean
	onRevoke: (user: CampaignUserData) => void
}

function AccessibleUsersTable({
	users,
	explicitMode,
	selfId,
	isLoading,
	isFetching,
	canManage,
	pendingUserId,
	disableActions,
	onRevoke,
}: AccessibleUsersTableProps) {
	const { t } = useTranslation('common')
	return (
		<div className="relative bg-white rounded-lg border border-grey-200 overflow-hidden">
			{isLoading
				? (
					<div className="flex items-center justify-center py-12">
						<div className="h-6 w-6 border-2 border-grey-200 border-t-brand-500 rounded-full animate-spin" />
					</div>
				)
				: (
					<>
						{isFetching && (
							<div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
								<div className="h-6 w-6 border-2 border-grey-200 border-t-brand-500 rounded-full animate-spin" />
							</div>
						)}
						<table className="w-full text-left">
							<thead>
								<tr className="border-b border-grey-200 bg-grey-50">
									<th className="py-3 px-5 text-sm font-medium text-grey-600">
										{t('overview.access.name')}
									</th>
									<th className="py-3 px-5 text-sm font-medium text-grey-600">
										{t('overview.access.email')}
									</th>
									<th className="py-3 px-5 text-sm font-medium text-grey-600">
										{t('overview.access.role')}
									</th>
									{canManage && <th className="py-3 px-5 w-0" />}
								</tr>
							</thead>
							<tbody>
								{users.map((accessibleUser) => {
									const isSelf = accessibleUser.id === selfId
									const isAdmin = accessibleUser.role === 'admin'
									const removable = !isSelf && !isAdmin
									const isRevoking = pendingUserId === accessibleUser.id

									return (
										<tr
											key={accessibleUser.id}
											className="border-b border-grey-100 last:border-b-0"
										>
											<td className="py-3.5 px-5 text-sm">
												{displayName(accessibleUser)}
											</td>
											<td className="py-3.5 px-5 text-sm text-grey-600">
												{accessibleUser.email}
											</td>
											<td className="py-3.5 px-5 text-sm text-grey-600">
												{roleLabel(accessibleUser.role)}
											</td>
											{canManage && (
												<td className="py-3.5 px-5 text-right">
													{removable
														? (
															<button
																type="button"
																disabled={disableActions}
																onClick={() => onRevoke(accessibleUser)}
																className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-red-600 rounded-md hover:bg-red-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
															>
																{isRevoking
																	? (
																		<div className="size-3.5 border border-red-300 border-t-red-500 rounded-full animate-spin" />
																	)
																	: (
																		<div className="size-4">
																			<Glyphs.heroiconsMicro.MinusCircleIcon />
																		</div>
																	)}
																<span>{t('overview.access.revoke')}</span>
															</button>
														)
														: (
															<Tooltip
																content={isSelf
																	? t('overview.access.lockedSelf')
																	: t('overview.access.lockedAdmin')}
															>
																<span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-grey-400">
																	<div className="size-3.5">
																		<Glyphs.heroiconsMicro.LockClosedIcon />
																	</div>
																	<span>{t('overview.access.locked')}</span>
																</span>
															</Tooltip>
														)}
												</td>
											)}
										</tr>
									)
								})}
								{users.length === 0 && (
									<tr>
										<td
											colSpan={canManage ? 4 : 3}
											className="py-12 text-center text-sm text-grey-500"
										>
											{explicitMode
												? t('overview.access.noUsers')
												: t('overview.access.noRecruiters')}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</>
				)}
		</div>
	)
}

type Member = {
	id: string
	firstname: string | null
	lastname: string | null
	email: string
	role: string | null
}

type GrantAccessDialogProps = {
	open: boolean
	onClose: () => void
	enabled: boolean
	explicitMode: boolean
	accessibleUserIds: Set<string>
	pendingUserId: string | null
	disableActions: boolean
	onGrant: (member: Member) => void
}

function GrantAccessDialog({
	open,
	onClose,
	enabled,
	explicitMode,
	accessibleUserIds,
	pendingUserId,
	disableActions,
	onGrant,
}: GrantAccessDialogProps) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { data: membersData, isLoading } = useOrganisationMembershipsIndex(
		organisationId,
		{ perPage: 100, page: 1 },
		{ enabled: enabled && open },
	)

	const grantableMembers = (membersData?.data ?? []).filter((member) => !accessibleUserIds.has(member.id))

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/20 backdrop-blur-sm"
					/>
					<div className="fixed inset-0 w-screen overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4">
							<DialogPanel
								as={motion.div}
								initial={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
								animate={{ opacity: 1, scale: 1, transition: { duration: 0.2 } }}
								exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
								className="w-full max-w-2xl rounded-xl bg-white shadow-lg ring-1 ring-grey-200/50 overflow-hidden"
							>
								<div className="px-6 py-5 border-b border-grey-100 flex items-center justify-between">
									<DialogTitle className="text-lg font-medium text-grey-900">
										{t('overview.access.grantAccess')}
									</DialogTitle>
									<button
										type="button"
										onClick={onClose}
										className="text-grey-400 hover:text-grey-600 cursor-pointer size-5"
										aria-label={t('overview.access.close')}
									>
										<Glyphs.nova.CloseIcon />
									</button>
								</div>
								<div className="max-h-[60vh] overflow-y-auto">
									{isLoading
										? (
											<div className="flex items-center justify-center py-12">
												<div className="h-6 w-6 border-2 border-grey-200 border-t-brand-500 rounded-full animate-spin" />
											</div>
										)
										: grantableMembers.length === 0
										? (
											<div className="py-12 text-center text-sm text-grey-500">
												{t('overview.access.everyoneHasAccess')}
											</div>
										)
										: (
											<table className="w-full text-left">
												<thead>
													<tr className="border-b border-grey-200 bg-grey-50 sticky top-0">
														<th className="py-3 px-5 text-sm font-medium text-grey-600">
															{t('overview.access.name')}
														</th>
														<th className="py-3 px-5 text-sm font-medium text-grey-600">
															{t('overview.access.email')}
														</th>
														<th className="py-3 px-5 text-sm font-medium text-grey-600">
															{t('overview.access.role')}
														</th>
														<th className="py-3 px-5 w-0" />
													</tr>
												</thead>
												<tbody>
													{grantableMembers.map((member) => {
														const isGranting = pendingUserId === member.id
														const hasImplicitAccess = !explicitMode
															&& member.role !== roleEnum.recruiter

														return (
															<tr
																key={member.id}
																className="border-b border-grey-100 last:border-b-0"
															>
																<td className="py-3.5 px-5 text-sm">
																	{displayName(member)}
																</td>
																<td className="py-3.5 px-5 text-sm text-grey-600">
																	{member.email}
																</td>
																<td className="py-3.5 px-5 text-sm text-grey-600">
																	{roleLabel(member.role)}
																</td>
																<td className="py-3.5 px-5 text-right">
																	<button
																		type="button"
																		disabled={disableActions || hasImplicitAccess}
																		onClick={() => onGrant(member)}
																		className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-green-700 rounded-md hover:bg-green-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
																	>
																		{isGranting
																			? (
																				<div className="size-3.5 border border-green-300 border-t-green-500 rounded-full animate-spin" />
																			)
																			: (
																				<div className="size-4">
																					<Glyphs.heroiconsMicro.PlusCircleIcon />
																				</div>
																			)}
																		<span>
																			{hasImplicitAccess
																				? t('overview.access.alwaysHasAccess')
																				: t('overview.access.grant')}
																		</span>
																	</button>
																</td>
															</tr>
														)
													})}
												</tbody>
											</table>
										)}
								</div>
							</DialogPanel>
						</div>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
